import type { EnrichedAdInputField, ItemResponse, Violation } from '@finn-no/mobility-item-insertion-shared';
import { useLingui } from '@lingui/react';
import { getCategoryForAdViewType } from '@m10s/mobility-mapping-utils';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, type SubmitErrorHandler, type SubmitHandler, useForm } from 'react-hook-form';
import type { WidgetModelArray } from '../../types/widget.js';
import { ErrorSummary } from '../components/error-summary.js';
import { FormActions } from '../components/form-actions.js';
import { StaleItemModal } from '../components/modals/stale-item.js';
import { WidgetFactory } from '../components/widget-factory.js';
import { useDebouncedCallback } from '../hooks/useDebouncedCallback.js';
import { useErrorTracking } from '../hooks/useErrorTracking.js';
import { useFormReadyFlag } from '../hooks/useFormReadyFlag.js';
import { useHotjar } from '../hooks/useHotjar.js';
import { useItemUpdateMutation } from '../hooks/useItemUpdater.js';
import { useMessageBus } from '../hooks/useMessageBus.js';
import { usePulse } from '../hooks/usePulse.js';
import { useToast } from '../hooks/useToast.js';
import { useUnsavedChangesWarning } from '../hooks/useUnsavedChangesWarning.js';
import { isFieldDirty } from '../utils/dirtyFieldsHelper.js';
import { getHuckConfig } from '../utils/getHuckConfig.js';
import { getInitialValues } from '../utils.js';

export const FormPage: React.FC = () => {
  const config = getHuckConfig();
  const { showToast } = useToast();
  const { trackPageView, trackClick } = usePulse(config.item);
  const { trackViolationErrors, trackValidationErrors, convertFieldErrorsToErrorInfo } = useErrorTracking({
    category: getCategoryForAdViewType(config.item.model),
  });
  const { i18n } = useLingui();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showStaleItemModal, setShowStaleItemModal] = useState(false);
  const remoteViolationsRef = useRef<Violation[]>([]);
  const [hasSubmitAttempted, setHasSubmitAttempted] = useState(false);
  const isMounted = useRef(false);

  const initialValues = getInitialValues({
    adInputLookup: config.adInputLookup,
    item: config.item,
    brand: config.brand,
    widgetModel: config.widgetModel as WidgetModelArray,
  }) as unknown as ItemResponse['data'];

  const methods = useForm<ItemResponse['data']>({
    values: initialValues,
    criteriaMode: 'all',
    mode: 'onBlur',
  });

  // Subscribe to formState.dirtyFields by reading it during render
  // This is required for React Hook Form's Proxy to track updates
  const { isDirty, dirtyFields } = methods.formState;
  const isFormDirty = isDirty && config.mode === 'edit';

  useUnsavedChangesWarning({ isDirty: isFormDirty, isSubmitting });

  const updateItem = useItemUpdateMutation(config.item?.etag ?? '');

  useMessageBus(methods);
  useHotjar();

  useEffect(() => {
    trackPageView();
  }, []);
  useFormReadyFlag();

  const showSaveSuccess = () =>
    showToast({
      type: 'success',
      message: i18n.t({
        id: 'mobility-item-insertion.form.auto-save.alert.label',
        comment: 'Alert message shown when an item/ad is auto-saved',
        message: 'The ad has been saved',
      }),
    });

  const showSaveFailure = () =>
    showToast({
      type: 'error',
      message: i18n.t({
        id: 'mobility-item-insertion.form.auto-save-failed.alert.label',
        comment: 'Alert message shown when an item/ad failed to auto-save',
        message: 'Failed to save the ad. Please try again later.',
      }),
    });

  const saveItem = async (options: { showAllErrors?: boolean } = {}) => {
    const { showAllErrors = hasSubmitAttempted } = options;
    const data = methods.getValues();
    try {
      const updateResult = await updateItem({ data, commit: false });
      if (updateResult?.violations) {
        remoteViolationsRef.current = updateResult.violations;
      } else {
        remoteViolationsRef.current = [];
      }
      // Show errors only for dirty fields, OR all if submit was attempted
      updateFormErrors({ showAllErrors });
      showSaveSuccess();
    } catch (e: unknown) {
      if ((e as { cause?: number }).cause === 412) {
        setShowStaleItemModal(true);
      } else {
        showSaveFailure();
      }
    }
  };

  const updateFormErrors = (options: { showAllErrors?: boolean } = {}) => {
    const { showAllErrors = false } = options;

    const isValidatedRemotely = (violation: Violation) => {
      if (violation.field === 'image') return true;

      const [parentKey, childKey] = violation.field.split('.');
      let modelField: EnrichedAdInputField | undefined = config.adInputLookup[parentKey];
      if (modelField?.children) {
        modelField = modelField.children[childKey];
      }
      if (!modelField) {
        return false;
      }
      return !modelField.rules || modelField.rules?.every((rule) => rule.type !== violation.type);
    };

    for (const violation of remoteViolationsRef.current) {
      if (isValidatedRemotely(violation)) {
        // Only set error if submit was attempted (showAllErrors) OR field is dirty
        if (showAllErrors || isFieldDirty(violation.field, dirtyFields as Record<string, unknown>)) {
          // @ts-expect-error types need some adjustment
          methods.setError(violation.field, {
            type: violation.type,
            message: violation.message,
          });
        }
      }
    }
  };

  const handleValidFormSubmission: SubmitHandler<ItemResponse['data']> = async (formData) => {
    setHasSubmitAttempted(true);
    setIsSubmitting(true);
    try {
      trackClick(
        {
          intent: config.mode === 'create' ? 'Create' : 'Save',
          object: {
            elementType: 'Button',
          },
        },
        { data: formData },
      );

      const data = await updateItem({ data: formData, commit: true });

      const violations: Violation[] | undefined = data?.violations;

      if (violations && violations.length > 0) {
        remoteViolationsRef.current = violations;
        updateFormErrors({ showAllErrors: true });
        trackViolationErrors(violations);
      } else {
        showSaveSuccess();

        const params = new URLSearchParams({
          mode: config.mode,
          model: data?.model ?? '',
        });

        if (config.orgId) {
          params.set('orgId', config.orgId);
        }

        const redirectResponse = await fetch(`${config.root}/api/redirect/${config.itemId}?${params.toString()}`);

        const redirectData = await redirectResponse.json();
        if (redirectData.url) {
          window.location.assign(redirectData.url);
        }
      }
    } catch (e: unknown) {
      if ((e as { cause?: number }).cause === 412) {
        setShowStaleItemModal(true);
      } else {
        console.error('Unexpected error when submitting a valid form', e);
        methods.setError('root.serverError', {
          message: i18n.t({
            id: 'mobility-item-insertion.form.server.error',
            comment: 'Error message shown when a server error occurs during form submission',
            message: 'An unexpected error occurred. Please try again.',
          }),
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInvalidFormSubmission: SubmitErrorHandler<ItemResponse['data']> = async (fieldErrors) => {
    setHasSubmitAttempted(true);
    await saveItem({ showAllErrors: true });

    // Track client-side validation errors
    const clientErrors = convertFieldErrorsToErrorInfo(fieldErrors);
    if (clientErrors.length > 0) {
      trackValidationErrors(clientErrors);
    }

    // Track server violations separately
    if (remoteViolationsRef.current.length > 0) {
      trackViolationErrors(remoteViolationsRef.current);
    }
  };

  const handleAbort = () => {
    return window.location.assign(`/my-items/details/${config.itemId}${config.orgId ? `?orgId=${config.orgId}` : ''}`);
  };

  const debouncedHandleAutoSave = useDebouncedCallback(() => saveItem(), 2000);

  useEffect(() => {
    if (config.mode !== 'edit') {
      const cb = methods.subscribe({
        formState: { values: true },
        callback: () => {
          if (!isMounted.current) {
            isMounted.current = true;
            return;
          }
          debouncedHandleAutoSave();
        },
      });

      return () => cb();
    }
  }, [methods.watch, debouncedHandleAutoSave, config.mode]);

  return (
    <main className="mb-32 max-w-[700]" style={{ margin: '0 auto' }}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleValidFormSubmission, handleInvalidFormSubmission)}>
          <WidgetFactory widgetModel={config.widgetModel as WidgetModelArray} adInputLookup={config.adInputLookup} />
          <ErrorSummary adInputLookup={config.adInputLookup} widgetModel={config.widgetModel as WidgetModelArray} />
          <FormActions
            onSubmit={methods.handleSubmit(handleValidFormSubmission, handleInvalidFormSubmission)}
            onAbort={handleAbort}
            item={config.item}
          />
        </form>
        <StaleItemModal open={showStaleItemModal} onDismiss={() => setShowStaleItemModal(false)} />
      </FormProvider>
    </main>
  );
};
